import type { ReactNode } from 'react';
import { formatCents, cn } from '@/lib/utils';

export interface ReportColumn {
  key: string;
  header: string;
  money?: boolean;
  align?: 'left' | 'right';
  render?: (value: unknown, row: Record<string, unknown>) => ReactNode;
}

interface ReportTableProps {
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  totalsRow?: Record<string, unknown>;
  keyField?: string;
}

function formatCell(value: unknown, col: ReportColumn): string {
  if (value === null || value === undefined) return '—';
  if (col.money && typeof value === 'number') return formatCents(value);
  return String(value);
}

export function ReportTable({ columns, rows, totalsRow, keyField }: ReportTableProps) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            {columns.map((col) => {
              const isRight = col.align === 'right' || col.money;
              return (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-3 font-medium text-muted-foreground',
                    isRight ? 'text-right' : 'text-left',
                  )}
                >
                  {col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-muted-foreground">
                No data.
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={keyField && row[keyField] != null ? String(row[keyField]) : i}
                className="hover:bg-muted/30 transition-colors"
              >
                {columns.map((col) => {
                  const isRight = col.align === 'right' || col.money;
                  return (
                    <td
                      key={col.key}
                      className={cn('px-4 py-3', isRight ? 'text-right tabular-nums' : 'text-left')}
                    >
                      {col.render ? col.render(row[col.key], row) : formatCell(row[col.key], col)}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
        {totalsRow && rows.length > 0 && (
          <tfoot>
            <tr className="border-t bg-muted/60">
              {columns.map((col) => {
                const isRight = col.align === 'right' || col.money;
                const val = totalsRow[col.key];
                return (
                  <td
                    key={col.key}
                    className={cn(
                      'px-4 py-3 font-semibold',
                      isRight ? 'text-right tabular-nums' : 'text-left',
                    )}
                  >
                    {val !== undefined && val !== null ? formatCell(val, col) : ''}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
